import { useId } from 'react';
import { MotifLayer } from './badgeArt';

/**
 * A badge, composed rather than drawn.
 *
 * Nothing about a badge's appearance is in this bundle. The server sends
 * `shape`, `motif` and two colours; this file knows how to assemble those into
 * a collectible. That is what lets a badge seeded next year render correctly in
 * a client built today — the alternative, a lookup table of forty badge keys to
 * forty pieces of art, would need a redeploy for every new badge and would be
 * the thing that quietly caps the catalog at whatever fits in one file.
 *
 * Every badge is four stacked layers:
 *
 *   1. the **plate** — the silhouette, filled with a vertical gradient of the
 *      badge's own two colours, so a Water badge is unmistakably blue before
 *      you have read a word of it;
 *   2. the **frame** — a metallic rim whose gradient is the *tier*. This is the
 *      part that carries progression: bronze reads warm and dull, diamond reads
 *      cold and bright, and you can tell them apart across a room;
 *   3. the **motif** — the emblem bitmap, in the accent colour;
 *   4. the **treatment** — shine sweep, glow, and at the top tier a rainbow
 *      holographic wash and orbiting sparks.
 *
 * Unearned badges render at tier 0: same shape, same emblem, no colour. Showing
 * the silhouette of what you have not got yet is the entire reason the
 * collection page is worth browsing.
 */

export type BadgeShape = 'shield' | 'hex' | 'gem' | 'star' | 'circle' | 'wreath' | 'flame' | 'crest';

export interface BadgeVisual {
  key: string;
  name: string;
  shape: string;
  motif: string;
  colorBase: string;
  colorAccent: string;
  tier: number;
  tierName?: string | null;
  isTopTier?: boolean;
}

/**
 * Tier metals. Two stops and a rim colour each.
 *
 * Chosen so the ladder reads as a ladder even in greyscale: value climbs from
 * bronze to diamond, and Master abandons metal for iridescence entirely, which
 * is what makes a mastered badge visible at a glance in a grid of forty.
 */
const METALS: Record<number, { light: string; dark: string; rim: string; label: string }> = {
  0: { light: '#8d9aa2', dark: '#5c686f', rim: '#47535a', label: 'Unearned' },
  1: { light: '#d8945a', dark: '#8c5024', rim: '#6b3c1a', label: 'Bronze' },
  2: { light: '#e8edf2', dark: '#96a3ad', rim: '#75828c', label: 'Silver' },
  3: { light: '#ffdc7a', dark: '#c08c14', rim: '#8f6608', label: 'Gold' },
  4: { light: '#e6f4ff', dark: '#7fa8c4', rim: '#4d7a94', label: 'Platinum' },
  5: { light: '#d9fbff', dark: '#63c8db', rim: '#2f8ea0', label: 'Diamond' },
  6: { light: '#fff3d0', dark: '#f0b429', rim: '#a9750a', label: 'Master' },
};

/**
 * The silhouettes, as SVG paths on a 100x100 canvas.
 *
 * Deliberately not all convex and not all the same width: a cabinet of eight
 * identical circles in different colours is a colour swatch chart, and the
 * whole ask was that these feel collectible. The shapes are what make the
 * Regional crest read differently from the Type hex at thumbnail size.
 */
const SHAPES: Record<string, string> = {
  // A gym-badge shield: broad shoulders, drawn to a point.
  shield: 'M50 4 L92 20 L92 52 Q92 82 50 96 Q8 82 8 52 L8 20 Z',
  // Flat-top hexagon — the enamel-pin standard.
  hex: 'M50 4 L90 27 L90 73 L50 96 L10 73 L10 27 Z',
  // A cut gem: table, crown and pavilion.
  gem: 'M28 10 L72 10 L94 38 L50 96 L6 38 Z',
  // Five points, thickened so the emblem still fits in the middle.
  star: 'M50 3 L62 35 L96 37 L69 58 L79 92 L50 72 L21 92 L31 58 L4 37 L38 35 Z',
  circle: 'M50 3 A47 47 0 1 1 49.9 3 Z',
  // A laurel-ish rounded square: soft, for the badges about tending things.
  wreath: 'M50 4 Q78 4 88 20 Q96 34 96 50 Q96 68 86 82 Q74 96 50 96 Q26 96 14 82 Q4 68 4 50 Q4 34 12 20 Q22 4 50 4 Z',
  flame: 'M50 2 Q66 24 66 38 Q78 32 80 20 Q94 44 94 60 Q94 96 50 96 Q6 96 6 60 Q6 44 20 20 Q22 32 34 38 Q34 24 50 2 Z',
  // A heraldic crest: square shoulders, scalloped base.
  crest: 'M12 6 L88 6 L88 46 Q88 66 74 78 Q62 89 50 96 Q38 89 26 78 Q12 66 12 46 Z',
};

const shapePath = (shape: string) => SHAPES[shape] ?? SHAPES.shield!;

export function Badge({
  badge,
  size = 96,
  /** Suppresses the idle animation — used in dense grids and on the pin picker. */
  still = false,
  title,
}: {
  badge: BadgeVisual;
  size?: number;
  still?: boolean;
  title?: string;
}) {
  // Gradient ids must be unique per instance or the first badge on the page
  // wins and every later one inherits its palette.
  const uid = useId().replace(/:/g, '');
  const tier = Math.max(0, Math.min(6, badge.tier));
  const metal = METALS[tier] ?? METALS[0]!;
  const earned = tier > 0;
  const top = Boolean(badge.isTopTier) && earned;

  const path = shapePath(badge.shape);
  const base = earned ? badge.colorBase : '#6d7a82';
  const accent = earned ? badge.colorAccent : '#aab6bd';

  const className = [
    'badge',
    `badge--tier-${tier}`,
    earned ? 'badge--earned' : 'badge--locked',
    top ? 'badge--master' : '',
    still ? 'badge--still' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <svg
      className={className}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={title ?? `${badge.name}${badge.tierName ? `, ${badge.tierName} tier` : ', not earned'}`}
      style={{ '--badge-glow': badge.colorAccent, '--badge-metal': metal.light } as React.CSSProperties}
    >
      <defs>
        <linearGradient id={`plate-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} />
          <stop offset="45%" stopColor={base} />
          <stop offset="100%" stopColor={earned ? base : '#4a555c'} />
        </linearGradient>

        <linearGradient id={`metal-${uid}`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor={metal.light} />
          <stop offset="38%" stopColor={metal.dark} />
          <stop offset="52%" stopColor={metal.light} />
          <stop offset="100%" stopColor={metal.dark} />
        </linearGradient>

        {/* The rainbow only exists on mastered badges — building it always
            would put six unused gradient stops in every locked badge on a
            forty-badge page. */}
        {top && (
          <linearGradient id={`holo-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff5f6d" />
            <stop offset="20%" stopColor="#ffc371" />
            <stop offset="40%" stopColor="#7bed9f" />
            <stop offset="60%" stopColor="#70a1ff" />
            <stop offset="80%" stopColor="#c56cf0" />
            <stop offset="100%" stopColor="#ff5f6d" />
          </linearGradient>
        )}

        {/* The shine sweep: a soft diagonal band that the CSS animation slides
            across the plate. Clipped to the silhouette so it never leaks. */}
        <linearGradient id={`shine-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="45%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        <clipPath id={`clip-${uid}`}>
          <path d={path} />
        </clipPath>
      </defs>

      {/* 1. Plate. */}
      <path d={path} fill={`url(#plate-${uid})`} />

      {/* Inner shadow at the base, so the plate has depth rather than reading
          as a flat sticker. */}
      <path d={path} fill="none" stroke="rgba(0,0,0,.35)" strokeWidth="6" clipPath={`url(#clip-${uid})`} />

      {/* 2. Holographic wash, mastered badges only.
             Underneath the emblem, not over it: on a real foil card the
             iridescence is the *background* the art sits on. Painting it on top
             screen-blends the emblem away entirely, which is exactly what the
             first version did. */}
      {top && (
        <path
          className="badge__holo"
          d={path}
          fill={`url(#holo-${uid})`}
          clipPath={`url(#clip-${uid})`}
        />
      )}

      {/* 3. Motif, centred in a 16x16 field inset from the frame.
             Drawn twice: a pale copy offset one pixel down, then the dark one
             on top. That is a bevel — the emblem reads as stamped into the
             plate, and more usefully it stays legible against a plate of any
             colour, which matters when the palette is per-badge data rather
             than something this file chose. */}
      <g transform="translate(26 25) scale(3)">
        <g opacity="0.55" transform="translate(0 0.8)">
          <MotifLayer motif={badge.motif} fill="rgba(255,255,255,.85)" highlight="rgba(255,255,255,.95)" />
        </g>
        <MotifLayer
          motif={badge.motif}
          fill="rgba(0,0,0,.62)"
          highlight={earned ? accent : '#c6d0d6'}
        />
      </g>

      {/* 4. Shine sweep, over everything but the frame — a highlight travelling
             across the face of the badge includes its emblem. */}
      <g clipPath={`url(#clip-${uid})`}>
        <rect className="badge__shine" x="-100" y="-20" width="60" height="140" fill={`url(#shine-${uid})`} transform="rotate(18 50 50)" />
      </g>

      {/* 5. Metallic frame, drawn last so it sits over everything. */}
      <path d={path} fill="none" stroke={`url(#metal-${uid})`} strokeWidth="7" strokeLinejoin="round" />
      <path d={path} fill="none" stroke={metal.rim} strokeWidth="1.5" strokeLinejoin="round" opacity="0.9" />

      {/* 6. Orbiting sparks. Purely decorative, and the one thing that makes a
             mastered badge move in a way nothing else on the page does. */}
      {top && (
        <g className="badge__sparks" aria-hidden>
          <circle cx="50" cy="8" r="2.6" fill="#fffbe6" />
          <circle cx="88" cy="52" r="2" fill="#fffbe6" />
          <circle cx="20" cy="72" r="2.2" fill="#fffbe6" />
        </g>
      )}
    </svg>
  );
}

/** The tier's display name, for places that show a rank without a badge. */
export const tierLabel = (tier: number) => METALS[Math.max(0, Math.min(6, tier))]?.label ?? 'Unearned';
